import { useState } from 'react'
import { refMatches } from '../buildAnswer.js'
import { MethodBadge } from '../../ui/Badges.jsx'
import { useWidgetContext } from './context.js'
import { AttachedGotchas, Card, Seg, PendingLine, Watch, asList, systemName, useSystemStyle } from './primitives.jsx'
import w from './widgets.module.css'

/** #1 Pipe flow map: one pipe's steps per direction; click a step for its call. */
export function PipeFlow({ input, block }) {
  const { systemsMap } = useWidgetContext()
  const systemStyle = useSystemStyle()
  const flows = asList(input.flows)
  const [dir, setDir] = useState(0)
  const [sel, setSel] = useState(0)
  const flow = flows[Math.min(dir, flows.length - 1)]
  const steps = asList(flow?.steps)
  const step = steps[Math.min(sel, steps.length - 1)]
  const attached = asList(block.gotchas).filter((g) => step && refMatches(g.attach_to?.ref, step.op_id))

  return (
    <Card
      label={`${input.pipe ?? 'Pipe'} flow`}
      title={input.pipe ? `${input.pipe} pipe` : 'Pipe flow'}
      sub={input.complexity ? `${input.complexity} complexity` : null}
      right={
        flows.length > 1 ? (
          <Seg
            label="Direction"
            options={flows.map((f, i) => ({ value: i, label: f.direction_label ?? `Flow ${i + 1}` }))}
            value={dir}
            onChange={(i) => {
              setDir(i)
              setSel(0)
            }}
          />
        ) : flow?.direction_label ? (
          <span className={w.sub}>{flow.direction_label}</span>
        ) : null
      }
    >
      {steps.length ? (
        <div className={w.flow}>
          {steps.map((s, i) => (
            <FlowStep
              key={`${s.op_id}-${i}`}
              step={s}
              index={i}
              selected={i === sel}
              onSelect={() => setSel(i)}
              style={systemStyle(s.system)}
              systemLabel={systemName(systemsMap, s.system)}
            />
          ))}
        </div>
      ) : (
        <PendingLine />
      )}
      {step ? (
        <div className={w.detail}>
          {step.method || step.path ? (
            <div className={w.pathLine}>
              {step.method && step.method !== '—' ? <MethodBadge method={step.method} size="secondary" /> : null}
              <span>{step.path}</span>
            </div>
          ) : null}
          {step.note ? <div className={w.muted}>{step.note}</div> : null}
          {step.watch ? <Watch>{step.watch}</Watch> : null}
          <AttachedGotchas key={`${dir}-${sel}`} items={attached} />
        </div>
      ) : null}
    </Card>
  )
}

function FlowStep({ step, index, selected, onSelect, style, systemLabel }) {
  return (
    <>
      {index ? (
        <span className={w.flowArrow} aria-hidden="true">
          →
        </span>
      ) : null}
      <button type="button" className={w.flowStep} style={style} aria-pressed={selected} onClick={onSelect}>
        <span className={w.flowNum}>
          {index + 1} · {systemLabel}
        </span>
        <span className={w.flowLabel}>{step.label}</span>
        {step.op_id ? <code className={w.flowOp}>{step.op_id}</code> : null}
      </button>
    </>
  )
}
